// EXPERIMENT: psf_promote_or_share_qr_code_experiment — remove with experiment cleanup
import { useCallback, useEffect, useRef } from 'react';

import { getWindow } from '@change-corgi/core/window';

import type { QrCodeTracking, QrViewCloseReason, QrViewSource } from '#src/app/pages/psf/promoteOrShare/shared/hooks';

type Params = Readonly<{
	tracking: Pick<QrCodeTracking, 'trackViewed' | 'trackClosed'>;
	/** How the view opened, read once at mount for `psf_qr_view`. */
	source: QrViewSource;
	/** The slide-up's open state. The view ends as soon as it turns false, not after the exit animation. */
	isSlideUpOpen: boolean;
	/** Why the view is unmounting (back, poster), or `null` when nothing said, which ends it as `close`. */
	getExitReason: () => QrViewCloseReason | null;
	/** Whether a Save share sheet is open, read when the page is hidden. */
	isShareInFlight: () => boolean;
}>;

/** The view of this mount: when it started, and whether its close event fired. */
type View = { startedAt: number; ended: boolean };

/**
 * One QR view per mount: `psf_qr_view` when the view mounts, and one `psf_qr_close` with the dwell
 * time on the first exit, whichever comes first: back, Print poster, the slide-up closing, `pagehide`,
 * or the page being hidden while no Save share sheet is open. The iOS share and print sheets can hide
 * the page, so a hide during a share does not end the view. Once ended, the view fires nothing more,
 * even when the supporter comes back to it. A new mount is a new view.
 *
 * The PSF routes are isolated, so the redirect after a Save is a full page load: the page hides before
 * any unmount can read the exit reason. So `pagehide` ends the view as `redirect` once Save recorded it,
 * and a hide after that does not end the view, since the redirect waits while the page is hidden.
 */
export function useQrViewTracking({ tracking, source, isSlideUpOpen, getExitReason, isShareInFlight }: Params): void {
	// Latest-ref, so a new `tracking` identity (the share tracking data changed) restarts no listener
	// and ends no view.
	const latestRef = useRef({ tracking, source, getExitReason, isShareInFlight });
	useEffect(() => {
		latestRef.current = { tracking, source, getExitReason, isShareInFlight };
	});

	const viewRef = useRef<View | null>(null);
	const mountedRef = useRef(false);

	const endView = useCallback((reason: QrViewCloseReason) => {
		const view = viewRef.current;
		if (!view || view.ended) return;
		view.ended = true;
		latestRef.current.tracking.trackClosed(reason, Date.now() - view.startedAt);
	}, []);

	useEffect(() => {
		mountedRef.current = true;
		// Kept across StrictMode's effect re-run, so the view starts and fires once per mount.
		if (!viewRef.current) {
			viewRef.current = { startedAt: Date.now(), ended: false };
			latestRef.current.tracking.trackViewed(latestRef.current.source);
		}

		const win = getWindow();
		const isRedirecting = () => latestRef.current.getExitReason() === 'redirect';
		const onPageHide = () => endView(isRedirecting() ? 'redirect' : 'page_hide');
		const onVisibilityChange = () => {
			if (win.document.visibilityState !== 'hidden') return;
			if (latestRef.current.isShareInFlight() || isRedirecting()) return;
			endView('visibility_hidden');
		};
		win.addEventListener('pagehide', onPageHide);
		win.document.addEventListener('visibilitychange', onVisibilityChange);

		return () => {
			win.removeEventListener('pagehide', onPageHide);
			win.document.removeEventListener('visibilitychange', onVisibilityChange);
			mountedRef.current = false;
			// Read now: the next QR view open resets it.
			const reason = latestRef.current.getExitReason() ?? 'close';
			// Deferred, so StrictMode's simulated unmount, which re-runs the effect straight away, ends nothing.
			queueMicrotask(() => {
				if (!mountedRef.current) endView(reason);
			});
		};
	}, [endView]);

	useEffect(() => {
		if (!isSlideUpOpen) endView('close');
	}, [isSlideUpOpen, endView]);
}
